import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import { getBookingDetails } from '../services/api';
import { format } from 'date-fns';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7f9;
`;

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: #f5f7f9;
`;

const InvoiceCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-top: 2rem;
`;

const InvoiceHeader = styled.div`
  border-bottom: 2px solid #eee;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  color: #316C6E;
  margin: 0 0 1rem 0;
`;

const BookingId = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.9rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #316C6E;
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.p`
  color: #666;
  margin: 0 0 0.3rem 0;
  font-size: 0.9rem;
`;

const Value = styled.p`
  margin: 0;
  font-weight: 500;
`;

const PriceSection = styled.div`
  border-top: 2px solid #eee;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    font-weight: bold;
    font-size: 1.2rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #eee;
  }
`;

const GuestList = styled.div`
  margin-top: 1.5rem;
`;

const GuestCard = styled.div`
  background-color: ${props => props.isPrimary ? '#f8f9fa' : 'white'};
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const GuestBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: ${props => props.isPrimary ? '#316C6E' : '#6c757d'};
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const GuestSection = styled(Section)`
  margin-top: 2rem;
  border-top: 2px solid #eee;
  padding-top: 2rem;
`;

const BookingDetailsPage = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBookingDetails();
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      const data = await getBookingDetails(id);
      // Convert string amounts to numbers
      const processedData = {
        ...data,
        totalAmount: parseFloat(data.totalAmount),
        roomCost: parseFloat(data.roomCost),
        bookingFee: parseFloat(data.bookingFee)
      };
      setBooking(processedData);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      setError('Failed to load booking details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return (
    <PageWrapper>
      <Navbar />
      <PageContainer>
        <InvoiceCard>
          <div>Loading booking details...</div>
        </InvoiceCard>
      </PageContainer>
    </PageWrapper>
  );

  if (error) return (
    <PageWrapper>
      <Navbar />
      <PageContainer>
        <InvoiceCard>
          <div>{error}</div>
        </InvoiceCard>
      </PageContainer>
    </PageWrapper>
  );

  if (!booking) return (
    <PageWrapper>
      <Navbar />
      <PageContainer>
        <InvoiceCard>
          <div>Booking not found</div>
        </InvoiceCard>
      </PageContainer>
    </PageWrapper>
  );

  return (
    <PageWrapper>
      <Navbar />
      <PageContainer>
        <InvoiceCard>
          <InvoiceHeader>
            <Title>Booking Details</Title>
            <BookingId>Booking ID: {booking.amadeusBookingId}</BookingId>
          </InvoiceHeader>

          <Section>
            <SectionTitle>Hotel Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <Label>Hotel Name</Label>
                <Value>{booking.hotelName}</Value>
              </InfoItem>
              <InfoItem>
                <Label>Room Type</Label>
                <Value>{booking.roomType}</Value>
              </InfoItem>
              <InfoItem>
                <Label>Check-in Date</Label>
                <Value>{format(new Date(booking.checkInDate), 'MMM dd, yyyy')}</Value>
              </InfoItem>
              <InfoItem>
                <Label>Check-out Date</Label>
                <Value>{format(new Date(booking.checkOutDate), 'MMM dd, yyyy')}</Value>
              </InfoItem>
              <InfoItem>
                <Label>Number of Rooms</Label>
                <Value>{booking.numberOfRooms}</Value>
              </InfoItem>
            </InfoGrid>
          </Section>

          <GuestSection>
            <SectionTitle>Guest Information</SectionTitle>
            <GuestList>
              {booking.Guests && booking.Guests.map((guest, index) => (
                <GuestCard key={index} isPrimary={index === 0}>
                  <GuestBadge isPrimary={index === 0}>
                    {index === 0 ? 'Primary Guest' : 'Secondary Guest'}
                  </GuestBadge>
                  <InfoGrid>
                    <InfoItem>
                      <Label>Full Name</Label>
                      <Value>{`${guest.firstName} ${guest.lastName}`}</Value>
                    </InfoItem>
                    <InfoItem>
                      <Label>Email</Label>
                      <Value>{guest.email}</Value>
                    </InfoItem>
                    <InfoItem>
                      <Label>Phone</Label>
                      <Value>{guest.phone}</Value>
                    </InfoItem>
                    <InfoItem>
                      <Label>Country</Label>
                      <Value>{guest.countryOfOrigin}</Value>
                    </InfoItem>
                    {guest.address && (
                      <InfoItem style={{ gridColumn: '1 / -1' }}>
                        <Label>Address</Label>
                        <Value>{guest.address}</Value>
                      </InfoItem>
                    )}
                  </InfoGrid>
                </GuestCard>
              ))}
            </GuestList>
          </GuestSection>

          <PriceSection>
            <SectionTitle>Payment Details</SectionTitle>
            <PriceItem>
              <span>Room Cost</span>
              <span>
                {booking.currency} {typeof booking.roomCost === 'number' 
                  ? booking.roomCost.toFixed(2) 
                  : '0.00'}
              </span>
            </PriceItem>
            <PriceItem>
              <span>Booking Fee</span>
              <span>
                {booking.currency} {typeof booking.bookingFee === 'number' 
                  ? booking.bookingFee.toFixed(2) 
                  : '0.00'}
              </span>
            </PriceItem>
            <PriceItem>
              <span>Total Amount</span>
              <span>
                {booking.currency} {typeof booking.totalAmount === 'number' 
                  ? booking.totalAmount.toFixed(2) 
                  : '0.00'}
              </span>
            </PriceItem>
          </PriceSection>
        </InvoiceCard>
      </PageContainer>
    </PageWrapper>
  );
};

export default BookingDetailsPage;
